import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {BaseComponentWithDrawer} from "../base-component-with-drawer.component";
import {
  AggregateInfo,
  MetricValueTypeForVirtualMetricEnum,
  VirtualMetric,
  VMDataTypeEnum,
  VMInput,
  VMResetIntervalEnum,
  VMResettableConfig,
  VMScheduledConfig,
  VMTypeEnum
} from "../../../models/virtualmetric/VirtualMetric";
import {RowClickEvent, RowPreparedEvent} from "devextreme/ui/data_grid";
import {ChangePartitionEvent} from "../../../events/ChangePartitionEvent";
import {PartitionSelectionService} from "../../../services/partition/partition-selection.service";
import {VirtualmetricOpeService} from "../../../services/ope/virtualmetricOpe.service";
import {GetVirtualMetricsCB} from "../../../ope/virtualmetric/GetVirtualMetricsCB";
import {GetDataUnitsCB} from "../../../ope/data-unit/GetDataUnitsCB";
import {DeviceTypeOpeService} from "../../../services/ope/deviceTypeOpe.service";
import {DataUnit} from "../../../models/data-unit/DataUnit";
import {AggregateFunctionTypeEnum, BucketUnitEnum} from "../../../enums/dashboard/ChartsEnum";
import {MetricOpeService} from "../../../services/ope/metricOpe.service";
import {GetMetricInstancesCB} from "../../../ope/metric/GetMetricInstancesCB";
import {UserMetricInstance} from "../../../models/user/Metric/UserMetricInstance";
import {DxButtonTypes} from "devextreme-angular/ui/button";
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {EditModeEvent} from "../../../events/EditModeEvent";
import {
  PutVirtualMetricCB,
  VMScheduledConfigDTO as PutVMScheduledConfigDTO
} from "../../../ope/virtualmetric/PutVirtualMetricCB";
import {
  CheckVirtualMetricExpressionCB,
  VMResettableConfigDTO,
  VMScheduledConfigDTO as CheckVMScheduledConfigDTO
} from "../../../ope/virtualmetric/CheckVirtualMetricExpressionCB";
import * as uuid from 'uuid'
import {DelVirtualMetricCB} from "../../../ope/virtualmetric/DelVirtualMetricCB";
import {DuplicateVirtualMetricCB} from "../../../ope/virtualmetric/DuplicateVirtualMetricCB";
import {ClickEvent} from "devextreme/ui/button";
import {DelDataUnitCB} from "../../../ope/data-unit/DelDataUnitCB";
import {DataTypeGroupEnum} from "../../../enums/dashboard/DataTypeUnitEnum";
import {DxFormComponent, DxTextBoxComponent} from "devextreme-angular";
import {Partition} from "../../../models/partition/Partition";

@Component({
  selector: 'app-virtual-measures',
  templateUrl: './virtual-measures.component.html',
  styleUrls: ['./virtual-measures.component.css', '../../common/drawer-style.css', '../base-component-with-drawer.component.css']
})
export class VirtualMeasuresComponent extends BaseComponentWithDrawer implements OnInit {

  @ViewChild('vmForm') vmForm?: DxFormComponent;

  @ViewChild('expressionField') expressionField?: DxTextBoxComponent;
  /**
   * List of all the available options for bucket units
   */
  timeUnitValues: string[];
  /**
   * List of all the available options for aggregate functions
   */
  aggregateFunctionValues: string[];
  /**
   * List of all the available options for VM types
   */
  vmTypeValues: string[];
  /**
   * downwards counter used to assign an auto decremented id to new Data Units
   */
  customDataUnitId: number = -1;
  operationsTooltipVisible = false;
  isNewVM = false;
  /**
   * Array containing all the partition's virtual metrics
   * @protected
   */
  protected virtualMetrics: VirtualMetric[];
  /**
   *  Object containing the currently selected virtual metric
   */
  protected selectedMetric?: VirtualMetric;
  /**
   * Variable where we can store the unsaved state of the selected metric so that we can roll it back if needed.
   * @protected
   */
  protected savedMetric?: VirtualMetric;
  /**
   * Virtual metric being duplicated, opens the duplicate dialog when set
   */
  protected duplicatingMetric?: VirtualMetric;
  /**
   * Name for the new virtual metric, edited in the duplicate dialog
   */
  protected duplicateMetricName: string = '';
  protected get selectedPartitionId(): number | undefined {
    return this.partitionSelection.selectedPartitionId ?? undefined;
  }

  /**
   * Array containing all the available data units
   * @protected
   */
  protected dataUnitValues: DataUnit[] = [];
  /**
   * Array containing the data types of the virtual metrics (DECIMAL / BOOL)
   * @protected
   */
  protected dataTypeValues: any[] = [];
  /**
   * Service for the virtual metric API calls
   * @protected
   */
  protected virtualMetricOpeService = inject(VirtualmetricOpeService);
  /**
   * Service for the data unit API calls
   * @protected
   */
  protected deviceTypeOpeService: DeviceTypeOpeService = inject(DeviceTypeOpeService);
  /**
   * Service for the data metric API calls
   * @protected
   */
  protected metricOpeService: MetricOpeService = inject(MetricOpeService);
  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService = inject(DashboardService);
  protected partitionSelection = inject(PartitionSelectionService);
  /**
   * Validation flag for the edit form
   */
  protected isEditFormValid: boolean;
  /**
   * Flag signaling to show the add metric popup
   */
  protected addMetricPopupVisible: boolean;
  /**
   * Flag signaling data is being loaded
   */
  protected loadingData: boolean;
  /**
   * The id of the gateway-device selected in the popup
   */
  protected selectedDeviceId: number | null;
  /**
   * List of ids selected in the popup form (only one will be used at a time but the component outputs an array)
   */
  protected selectedMetricIds: (number | undefined)[];
  /**
   * Object containing the data of the metric popup form
   */
  protected metricToAdd: VMInput;
  /**
   * List of all available options for the reset interval selection
   */
  protected vmResetIntervalValues: string[];
  /**
   * List of all the metrics of the current VM
   */
  protected selectedMetrics: VMInput[];
  /**
   * Variable where we can store the unsaved state of the selected metrics so that we can roll it back if needed.
   * @protected
   */
  protected savedMetrics: VMInput[];
  /**
   * Wrapper variable to use the enum in the HTML
   * @protected
   */
  protected readonly VMTypeEnum = VMTypeEnum;
  protected metricValueType: string[];
  protected functionTypesValues: string[];
  /**
   * Index of the currently selected tab
   * @protected
   */
  protected selectedTab = 0;
  protected isExpressionValid = false;
  protected readonly DataTypeGroupEnum = DataTypeGroupEnum;
  protected operationsTooltipButton: DxButtonTypes.Properties = {
    icon: 'info',
    stylingMode: 'text',
    onClick: () => {
      this.operationsTooltipVisible = !this.operationsTooltipVisible;
    },
  };
  /**
   * Flag signaling to show the operators and functions popup
   */
  protected expressionDocPopupOpened: boolean = false;
  protected expressionErrorMessage: string = "";

  constructor() {
    super();
    this.virtualMetrics = [];
    this.isEditMode = false;
    this.isEditFormValid = true;
    this.addMetricPopupVisible = false;
    this.loadingData = false;
    this.selectedMetricIds = [];
    this.selectedDeviceId = null;
    this.metricToAdd = new VMInput();
    this.selectedMetrics = [];
    this.savedMetrics = [];
    this.timeUnitValues = Object.values(BucketUnitEnum);
    this.aggregateFunctionValues = Object.values(AggregateFunctionTypeEnum);
    this.vmTypeValues = Object.values(VMTypeEnum);
    this.vmResetIntervalValues = Object.values(VMResetIntervalEnum);
    this.metricValueType = Object.values(MetricValueTypeForVirtualMetricEnum);
    this.functionTypesValues = Object.values(AggregateFunctionTypeEnum);

    this.dataTypeValues = [
      {
        label: 'REGISTRY.VIRTUAL_METRICS.DATA_TYPE.NUMERIC',
        value: VMDataTypeEnum.NUMERIC
      },
      {
        label: 'REGISTRY.VIRTUAL_METRICS.DATA_TYPE.BOOLEAN',
        value: VMDataTypeEnum.BOOLEAN
      }
    ];

  }

  /**
   * Flag signaling edit mode is active
   */
  private _isEditMode: boolean = false;

  get isEditMode() {
    return this._isEditMode;
  }

  set isEditMode(value: boolean) {
    this.isExpressionValid = true;
    this.isEditFormValid = true;
    if (value) {
      if (this._isEditMode !== value) {
        this.savedMetric = this.appService.deepCopy(this.selectedMetric);
        this.savedMetrics = this.appService.deepCopy(this.selectedMetrics);
      }
    }
    if (!value) {
      this.selectedTab = 0;
    }

    this._isEditMode = value;
    this.eventBus.emitEvent(new EditModeEvent(value));
  }

  /**
   *
   */
  ngOnInit() {
    super.ngOnInit();
    this.loadVirtualMetrics();
    this.subscriptions = [
      this.eventBus.onEvent(ChangePartitionEvent.NAME, () => {
        this.loadVirtualMetrics();
      })
    ]
  }

  /**
   * Gets all the current partition's virtual metrics
   */
  loadVirtualMetrics() {
    this.loadingData = true;
    this.getDataUnits();
    if (this.selectedPartitionId !== undefined) {
      const cb = new GetVirtualMetricsCB();
      cb.i.partitionId = this.selectedPartitionId;
      this.virtualMetricOpeService.getVirtualMetrics(cb).then(() => {
          this.virtualMetrics = cb.o.virtualMetrics;
          this.loadingData = false;
        }
      );
    }
  }

  /**
   * Opens the create and edit drawer
   */
  showNewVirtualMetricForm() {
    this.isNewVM = true;
    this.isDrawerOpen = true;
    this.selectedMetric = new VirtualMetric();
    this.selectedMetrics = [];
    this.isEditMode = true;
    this.setDataTypeFromBoolFlag();
  }

  /**
   * Opens the Edit/Detail drawer for the clicked metric
   * @param $event
   */
  onVirtualMetricRowClick($event: RowClickEvent) {
    this.isNewVM = false;
    this.isDrawerOpen = true;
    this.selectedMetric = this.appService.deepCopy($event.data);
    this.selectedMetrics = [];
    this.setDataTypeFromBoolFlag();
    if (!!this.selectedMetric) {
      this.selectedMetric.isResettable = this.selectedMetric.type === VMTypeEnum.RESETTABLE;
      if (!this.selectedMetric!.scheduledConfig) {
        this.selectedMetric!.scheduledConfig = new VMScheduledConfig();
      }
      if (!this.selectedMetric!.resettableConfig) {
        this.selectedMetric!.resettableConfig = new VMResettableConfig();
      }
      let metricsToGet: number[] = [];
      if (this.selectedMetric.type === VMTypeEnum.SCHEDULED) {
        if (!!this.selectedMetric.scheduledConfig) {
          metricsToGet = this.selectedMetric.scheduledConfig.inputMetrics.map(x => x.metricInstanceId);
        }
      } else if (this.selectedMetric.type === VMTypeEnum.RESETTABLE) {
        if (!!this.selectedMetric.resettableConfig) {
          metricsToGet.push(this.selectedMetric.resettableConfig.metricInstanceId);
        }
      }
      if (metricsToGet.length > 0) {
        const cb = new GetMetricInstancesCB();
        cb.i.metricInstanceIds = metricsToGet;
        this.metricOpeService.getMetricInstances(cb).then(
          () => {
            this.selectedMetrics = [];
            if (this.selectedMetric!.type === VMTypeEnum.SCHEDULED) {
              this.selectedMetric?.scheduledConfig?.inputMetrics.forEach(x => {
                let localMetric = this.appService.deepCopy(x);
                let idx = cb.o.metricInstances.findIndex(mi => mi.id === localMetric.metricInstanceId);
                if (idx >= 0) {
                  localMetric.fullMetricInstance = cb.o.metricInstances[idx];
                }
                if (!!localMetric.aggregateInfo) {
                  localMetric.valueType = MetricValueTypeForVirtualMetricEnum.AGGREGATE
                } else {
                  localMetric.valueType = MetricValueTypeForVirtualMetricEnum.LAST_VALUE
                }
                localMetric.uuid = uuid.v4();
                this.selectedMetrics.push(localMetric);
              })
            } else if (this.selectedMetric!.type === VMTypeEnum.RESETTABLE) {
              if (cb.o.metricInstances.length > 0 && !!this.selectedMetric!.resettableConfig) {
                let localMetric = new VMInput();
                localMetric.metricInstanceId = this.selectedMetric!.resettableConfig?.metricInstanceId;
                localMetric.alias = this.selectedMetric!.resettableConfig?.alias;
                localMetric.fullMetricInstance = cb.o.metricInstances[0];
                this.selectedMetrics.push(localMetric);
              }
            }
          }
        );
      }
    }
  }

  /**
   * Starts the edit mode
   */
  enableEditMode() {
    this.isEditMode = true;
  }

  /**
   * Saves the virtual metric
   */
  async saveVirtualMetric() {

    if (!!this.vmForm && !!this.vmForm.instance) {
      this.vmForm.instance.validate();

      if (!this.isExpressionValid || !this.selectedMetric?.expression) {
        await this.validateExpression();
      }
      if (
        !!this.selectedMetric
        && this.selectedPartitionId !== undefined
        && this.vmForm.instance.validate().isValid
        && this.isExpressionValid
      ) {
        this.loadingData = true;
        let cb = new PutVirtualMetricCB();
        cb.i.id = this.selectedMetric.id;
        cb.i.partitionId = this.selectedPartitionId;
        cb.i.name = this.selectedMetric.name;
        cb.i.enabled = this.selectedMetric.enabled;
        cb.i.type = this.selectedMetric.type;
        cb.i.expression = this.selectedMetric.expression;
        cb.i.isBoolean = this.selectedMetric.dataType === VMDataTypeEnum.BOOLEAN;
        cb.i.decimalDigits = this.selectedMetric.decimalDigits !== undefined ? this.selectedMetric.decimalDigits : 0;

        if (!!this.selectedMetric.scheduledConfig && this.selectedMetric.type === VMTypeEnum.SCHEDULED) {
          this.selectedMetric.scheduledConfig.inputMetrics = this.selectedMetrics;
          cb.i.scheduledConfig = new PutVMScheduledConfigDTO(this.selectedMetric.scheduledConfig);
        } else if (!!this.selectedMetric.resettableConfig && this.selectedMetric.type === VMTypeEnum.RESETTABLE) {
          cb.i.resettableConfig = this.selectedMetric.resettableConfig;
          if (this.selectedMetrics.length > 0) {
            cb.i.resettableConfig.metricInstanceId = this.selectedMetrics[0].metricInstanceId;
            cb.i.resettableConfig.alias = this.selectedMetrics[0].alias;
            if (this.selectedMetrics[0].fullMetricInstance!.deviceId !== undefined && this.selectedMetrics[0].fullMetricInstance!.deviceId >= 0)
              cb.i.resettableConfig.deviceId = this.selectedMetrics[0].fullMetricInstance!.deviceId;
          }
        }


        if (!!this.selectedMetric.dataUnit) {
          let dataUnitObject = this.dataUnitValues.find(dataUnit => dataUnit.id === this.selectedMetric!.dataUnit!.id);
          if (!!dataUnitObject) {
            this.selectedMetric.dataUnit = dataUnitObject;
          }
          cb.i.dataUnit = {
            name: this.selectedMetric.dataUnit.name
          }
        }

        this.virtualMetricOpeService.putVirtualMetric(cb).then(
          () => {
            this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.VIRTUAL_METRICS.UPDATED"));
            this.isDrawerOpen = false;
            this.isEditMode = false;
            this.loadVirtualMetrics();
          }
        );
      }
    }
  }

  /**
   * Ends edit mode without saving
   */
  disableEditMode() {
    if (!this.appService.deepEquals(this.savedMetric, this.selectedMetric) || !this.appService.deepEquals(this.savedMetrics, this.selectedMetrics)) {
      this.showDiscardEditsDialog().show().done(
        (result: boolean) => {
          this.isEditMode = !result;
          if (result) {
            if (this.isNewVM) {
              this.isDrawerOpen = false;
            }
            this.selectedMetric = this.savedMetric;
            this.selectedMetrics = this.savedMetrics;
          }
        });
    } else {
      this.isEditMode = false;
    }
  }

  /**
   *
   */
  closeDrawer() {
    this.isDrawerOpen = false;
  }

  /**
   *
   */
  deleteVirtualMetric() {
    if (!!this.selectedMetric) {
      let result = this.showDeleteDialog(this.selectedMetric.name).show().done();
      result.then(async (dialogResult: any) => {
        if (dialogResult) {
          this.loadingData = true;
          const cb = new DelVirtualMetricCB();
          cb.i.id = this.selectedMetric!.id;
          this.virtualMetricOpeService.delVirtualMetric(cb).then(
            () => {
              this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.VIRTUAL_METRICS.DELETED"));
              this.isDrawerOpen = false;
              this.loadVirtualMetrics();
            }
          );
        }
      });
    }
  }

  /**
   * Open the duplicate virtual metric dialog, prefilling the new name
   */
  openDuplicateVirtualMetric(metric: VirtualMetric) {
    this.duplicatingMetric = metric;
    const suffix = ' - ' + this.translate.instant('REGISTRY.VIRTUAL_METRICS.TEXT.DUPLICATE_VM_NAME_SUFFIX');
    this.duplicateMetricName = metric.name.slice(0, Math.max(this.gmService.STR_DIM_MID - suffix.length, 0)) + suffix;
  }

  duplicateVirtualMetric() {
    const cb = new DuplicateVirtualMetricCB();
    cb.i.virtualMetricId = this.duplicatingMetric!.id;
    cb.i.name = this.duplicateMetricName;

    this.virtualMetricOpeService.duplicateVirtualMetric(cb).then(() => {
      this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.VIRTUAL_METRICS.DUPLICATED"));
      this.duplicatingMetric = undefined;
      this.loadVirtualMetrics();
    });
  }

  /**
   * Get all Data Units for metrics
   */
  getDataUnits() {
    const cb = new GetDataUnitsCB();

    this.deviceTypeOpeService.getDataUnits(cb).then((_) => {

      this.dataUnitValues = cb.o.metricInstances;

    });
  }

  goToMetricsTab() {
    this.selectedTab = 1;
  }

  openExpressionDocPopup() {
    this.expressionDocPopupOpened = true;
  }

  translateFunctionType = (functionType: string) => {
    if (!!functionType) {
      return this.translate.instant('CHART_CREATION.CHART_TYPES.' + functionType);
    }
    return null;
  };

  getAcquisitionCronLabel = (acquisitionCron: string) => {
    if (!!acquisitionCron) {
      let val = this.translate.instant('DATE_SELECTION.REALTIME.PLURAL.' + acquisitionCron);
      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }
    return null;
  };

  /**
   * Initialize detail form when the VM Type changes
   */
  initializeDetailForm = (value: any) => {
    if (!!this.selectedMetric) {
      if (value.value) {
        this.selectedMetric.type = VMTypeEnum.RESETTABLE;

        // data type is always numeric for resettable metrics
        this.selectedMetric.dataType = VMDataTypeEnum.NUMERIC;
        this.selectedMetric.isBool = false;

        this.selectedMetrics.forEach(
          metric => {
            metric.valueType = MetricValueTypeForVirtualMetricEnum.LAST_VALUE;
          }
        )

      } else {
        this.selectedMetric.type = VMTypeEnum.SCHEDULED;
      }
    }
  };

  /**
   * Event implementation for the datagrid reorder event
   * @param e
   */
  onReorder = (e: any) => {
    this.array_move(this.selectedMetrics, e.fromIndex, e.toIndex);
  };

  colorResettableRows(e: RowPreparedEvent) {
    if (this.selectedMetric?.type === VMTypeEnum.RESETTABLE) {
      if (e.rowType === "data") {
        if (e.rowIndex > 0) {
          e.rowElement.style.backgroundColor = "color-mix(in oklab, var(--dx-component-color-bg) 60%, var(--keton-alarm-offline))";
        }
      }
    }
  }

  onEditorPreparingMetric(e: any) {
    if (e.parentType !== 'dataRow' || !(e.cellTemplate === 'bucketValue' || e.cellTemplate === 'bucketUnits'
      || e.cellTemplate === 'aggregateFunction' || e.cellTemplate === 'samplePercentage')) {
      return
    }

    e.editorOptions.disabled = e.row.data.valueType !== MetricValueTypeForVirtualMetricEnum.AGGREGATE;
    e.editorOptions.placeholder = !e.row.data.aggregate ? 'Non disponibile' : '';

  }

  public setDataTypeFromBoolFlag() {
    if (!!this.selectedMetric) {
      if (this.selectedMetric.isBool) {
        this.selectedMetric.dataType = VMDataTypeEnum.BOOLEAN;
      } else {
        this.selectedMetric.dataType = VMDataTypeEnum.NUMERIC;
      }
    }

  }

  translateLabel = (object: any) => {
    if (!!object) return this.translate.instant(object.label);
    return '';
  };

  setDataTypeValue = (e: any) => {

    if (!!this.selectedMetric) {
      this.selectedMetric.isBool = e.value === VMDataTypeEnum.BOOLEAN;
      this.setDataTypeFromBoolFlag();
    }
  };

  convertSelectedMetric(selMetric: UserMetricInstance) {

    if (selMetric.dataTypeGroup === DataTypeGroupEnum.STRING) {
      this.toastService.openErrorToastMessage(this.translate.instant('INVALID_DATA_TYPE'));
      return;
    }
    this.isEditMode = true;
    let newMetric = new VMInput();
    newMetric.metricInstanceId = selMetric.id;
    newMetric.alias = this.generateAliasForMetric();
    newMetric.fullMetricInstance = selMetric;
    this.selectedMetrics.push(newMetric);

  }

  updateMetricOnChange(newData: VMInput, value: MetricValueTypeForVirtualMetricEnum, currentData: VMInput) {
    if (value === MetricValueTypeForVirtualMetricEnum.LAST_VALUE) {
      newData.aggregate = false;
      newData.aggregateInfo = null;
    } else {
      newData.aggregate = true;
      newData.aggregateInfo = new AggregateInfo();
    }

    (<any>this).defaultSetCellValue(newData, value, currentData);

  };

  composeFullMetricName(metric: UserMetricInstance) {
    let name;
    if (!!metric) {
      // Name is composed by PartitionName - DeviceName - MetricName
      name = metric.partitionName;
      if (!!metric.deviceName) {
        name += " > " + metric.deviceName;
      }
      name += " > " + metric.name;
    }
    return name;
  }

  validateAliasUniqueness = (row: any) => {
    row.data.alias;
    return this.selectedMetrics.filter(metric => metric.alias === row.data.alias && metric.uuid !== row.data.uuid).length === 0;
  };

  addNewDataUnit = (onCustomItemCreatingEvent: any) => {

    if (!onCustomItemCreatingEvent.text) {
      return;
    }

    const component = onCustomItemCreatingEvent.component;
    const dataSource = component.getDataSource();
    const dataStore = dataSource.store();

    // Cerca se l'item esiste già nel data store
    dataStore.load().then((items: any[]) => {
      let existingItem = items.find(item => item.name === onCustomItemCreatingEvent.text);

      if (existingItem) {
        // Se l'item esiste già, assegnalo all'evento
        onCustomItemCreatingEvent.customItem = existingItem;
      } else {
        // Se l'item non esiste, creane uno nuovo
        let newItem = {
          id: this.customDataUnitId--,
          name: onCustomItemCreatingEvent.text
        };

        onCustomItemCreatingEvent.customItem = newItem;

        dataStore.insert(newItem);
        dataSource.load();
      }

      let newDataUnit = new DataUnit();
      newDataUnit.id = onCustomItemCreatingEvent.customItem.id;
      newDataUnit.name = onCustomItemCreatingEvent.customItem.name;

      this.selectedMetric!.dataUnit = newDataUnit;
    });
  };

  deleteDataUnit(dataUnit: DataUnit, $event: ClickEvent) {
    $event.event?.stopPropagation();

    let result = this.showDeleteDialog(dataUnit.name).show().done();
    result.then(async (dialogResult: any) => {
      if (dialogResult) {
        if (dataUnit.id < 0) {
          this.dataUnitValues = this.dataUnitValues.filter(d => d.id !== dataUnit.id);
          if (!!this.selectedMetric && !!this.selectedMetric.dataUnit && dataUnit.id === this.selectedMetric?.dataUnit?.id) {
            delete this.selectedMetric.dataUnit;
          }

          this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.DEVICE_TYPE.TEXT.DATA_UNIT_DELETED"));
        } else {
          const cb = new DelDataUnitCB();
          cb.i.id = dataUnit.id;
          this.deviceTypeOpeService.delDataUnit(cb).then((_) => {
            this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.DEVICE_TYPE.TEXT.DATA_UNIT_DELETED"));
            if (!!this.selectedMetric && !!this.selectedMetric.dataUnit && dataUnit.id === this.selectedMetric?.dataUnit?.id) {
              delete this.selectedMetric.dataUnit;
            }
            this.getDataUnits();

          });
        }
      }
    });

  }

  validateExpression() {
    if (!!this.expressionField) {
      if (this.expressionField.value === "" || !this.expressionField?.value) {
        this.expressionField.validationStatus = "invalid";
        this.isExpressionValid = false;
        return
      }

      this.expressionField!.validationStatus = "pending";
      return new Promise((resolve, reject) => {

        if (!!this.selectedMetric) {
          const cb = new CheckVirtualMetricExpressionCB();
          cb.i.expression = this.selectedMetric.expression;
          cb.i.type = this.selectedMetric.type;
          cb.i.isBoolean = this.selectedMetric?.dataType === VMDataTypeEnum.BOOLEAN;
          if (!!this.selectedMetric.scheduledConfig && this.selectedMetric.type === VMTypeEnum.SCHEDULED) {
            this.selectedMetric.scheduledConfig.inputMetrics = this.selectedMetrics;
            cb.i.scheduledConfig = new CheckVMScheduledConfigDTO(this.selectedMetric.scheduledConfig);
          } else if (!!this.selectedMetric.resettableConfig && this.selectedMetric.type === VMTypeEnum.RESETTABLE) {
            if (this.selectedMetrics.length > 0) {
              cb.i.resettableConfig = new VMResettableConfigDTO();
              cb.i.resettableConfig.metricInstanceId = this.selectedMetrics[0].metricInstanceId;
              cb.i.resettableConfig.alias = this.selectedMetrics[0].alias;
            }
          }

          this.virtualMetricOpeService.checkVirtualMetricExpression(cb).then(() => {
            if (!!cb.o.warning) {
              this.expressionErrorMessage = cb.o.warning;
            }
            this.isExpressionValid = cb.o.valid;
            if (!!cb.o.warning)
              this.toastService.openErrorToastMessage(cb.o.warning);
            this.expressionField!.validationStatus = cb.o.valid ? "valid" : "invalid";
            resolve(cb.o.valid);
          }).catch(
            (err) => {
              this.expressionField!.validationStatus = "invalid";
              this.isExpressionValid = cb.o.valid;
              this.logger.error(err);
              reject(err);
            }
          );
        }
      });
    } else {
      return
    }
  }

  validateForm() {
    if (!!this.selectedMetric && !!this.vmForm) {
      const formValidation = this.vmForm.instance.validate();
      this.isEditFormValid = formValidation.isValid! && this.selectedMetrics.length > 0;
    }
  }

  translateResetIntervals = (interval: any) => {
    if (!!interval) {
      return this.translate.instant('REGISTRY.VIRTUAL_METRICS.RESET_INTERVALS.' + interval);
    } else {
      return interval;
    }
  };

  translateVirtualMetricType = (virtualMetric: VirtualMetric) => {
    return this.translate.instant('REGISTRY.VIRTUAL_METRICS.' + virtualMetric.type);
  };

  parseLastValue = (virtualMetric: VirtualMetric) => {
    if (virtualMetric.lastValue !== undefined && virtualMetric.lastValue !== null) {
      if (!virtualMetric.isBool) {
        if (!isNaN(virtualMetric.lastValue))
          return virtualMetric.lastValue.toFixed(virtualMetric.decimalDigits?? 0);
      } else {
        return this.appService.translateMaybeBoolean(virtualMetric.lastValue);
      }
    }
    if (!virtualMetric.lastValue) {
      return "---"
    } else {
      return virtualMetric.lastValue;
    }
  };

  getPartitionErrorMessage() {
    const selectedPartition = this.dashboardService.partitions.find(
      partition => partition.id === this.dashboardService.selectedPartitionIds[0]
    );

    const userPartition = this.dashboardService.partitions.find(
      partition => partition.id === this.selectedPartitionId
    );
    if (!!selectedPartition && !!userPartition) {
      return this.translate.instant('REGISTRY.VIRTUAL_METRICS.TEXT.PARTITION_VISIBILITY_ALERT',
        {
          selectedPartition: selectedPartition.name,
          userPartition: userPartition.name,
        }
      );
    }
  }

  partitionIsNotVisible() {

    // Return true if:
    // Selected partition is father of current
    // Selected partition is brother or not related to current

    // If I check current partition and all children and I dont find selected partition
    // Show the error message

    let partitionsToCheck = this.dashboardService.partitions.filter(
      partitionToFilter => {
        return partitionToFilter.id === this.selectedPartitionId;
      });
    if (this.dashboardService.selectedPartitionIds[0] !== undefined)
      return !this.checkChildrenForSpecificPartition(partitionsToCheck, this.dashboardService.selectedPartitionIds[0]);
    return false;
  }

  checkChildrenForSpecificPartition(partitionsToCheck: Partition[], targetPartition: number): boolean {
    for (const partition of partitionsToCheck) {
      if (partition.id === targetPartition) {
        return true
      } else {
        let childrenPartitions = this.dashboardService.partitions.filter(
          partitionToFilter => {
            return partitionToFilter.parentId === partition.id;
          }
        );
        if (this.checkChildrenForSpecificPartition(childrenPartitions, targetPartition)) {
          return true;
        }
      }
    }
    return false
  }

  protected parseMetricValueType = (value: string) => {
    return this.translate.instant('REGISTRY.VIRTUAL_METRICS.TABLE.VALUE_TYPES.' + value);
  };

  private getMaxAliasIndexFromSelectedMetrics(): number {
    if (!this.selectedMetrics || this.selectedMetrics.length === 0) {
      return 0;
    }

    // estraggo solo gli alias con formato <"M" + numero> (scartando quindi quelli customizzati), e rimuovo il prefisso "M"
    const aliasListWithoutPrefix = this.filterOriginalAliasFormatList();

    // restituisco il numero più grande tra quelli estratti
    if (aliasListWithoutPrefix.length > 0) {
      return Math.max(...aliasListWithoutPrefix.map((alias: string) => +alias));
    } else {
      return 0;
    }
  }

  private filterOriginalAliasFormatList(): string[] {
    return this.selectedMetrics.map((metric: VMInput) => metric.alias).filter((alias: string) => alias.startsWith("M") && !isNaN(+alias.substring(1))).map((alias: string) => alias.substring(1));

  }

  private generateAliasForMetric(): string {
    return "M" + (this.getMaxAliasIndexFromSelectedMetrics() + 1);
  }

}
